// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';

// Define types for user and context
interface User {
    id: string;
    username: string;
    email: string;
    role: string;
    name?: string;
}

// Define type for login response
interface LoginResponse {
    status?: string;
    message?: string;
    access_token?: string;
    token_type?: string;
    expires_in?: number;
    user?: {
        id: string;
        name?: string;
        username?: string;
        email?: string;
        role?: string;
    };
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    logout: () => void;
    checkAuthStatus: () => boolean;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    login: async () => {},
    logout: () => {},
    checkAuthStatus: () => false,
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component that wraps the app and makes auth object available
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // Check auth status helper function
    const checkAuthStatus = (): boolean => {
        // Check if there's a valid token
        if (!authService.isAuthenticated()) {
            return false;
        }

        // Get and validate user data
        const userData = authService.getUser();
        if (!userData || !userData.id) {
            authService.logout();
            return false;
        }

        return true;
    };

    // Initialize authentication on mount and path changes
    useEffect(() => {
        const initAuth = async () => {
            setIsLoading(true);
            try {
                // Check if there's a stored token and user data
                if (authService.isAuthenticated()) {
                    const userData = authService.getUser();

                    // Validate user data
                    if (userData && userData.id) {
                        setUser(userData);

                        // If on root path and authenticated, redirect to appropriate page
                        if (location.pathname === '/') {
                            const role = userData.role;
                            if (role === 'caissier') {
                                navigate('/cashier', { replace: true });
                            } else {
                                navigate('/admin', { replace: true });
                            }
                        }
                    } else {
                        // Invalid user data, log out
                        authService.logout();
                        setUser(null);

                        // Redirect to auth page if not already there
                        if (location.pathname !== '/') {
                            navigate('/', { replace: true });
                        }
                    }
                } else {
                    // No authentication, make sure user is null
                    setUser(null);

                    // Redirect to auth page if not already there and not a public path
                    if (location.pathname !== '/' && location.pathname !== '/home' && !location.pathname.includes('*')) {
                        navigate('/', { replace: true });
                    }
                }
            } catch (error) {
                console.error('Failed to initialize authentication:', error);
                // If there's an error, clear any invalid auth data
                authService.logout();
                setUser(null);

                // Redirect to auth page if not already there
                if (location.pathname !== '/') {
                    navigate('/', { replace: true });
                }
            } finally {
                setIsLoading(false);
            }
        };

        initAuth();
    }, [navigate, location.pathname]);

    // Login function
    const login = async (username: string, password: string) => {
        setIsLoading(true);
        try {
            const response = await authService.login({ username, password });

            // Check if we received user data and it has the required fields
            if (response.user) {
                // Create a valid User object with default values for optional fields
                const userData: User = {
                    id: response.user.id || '',
                    username: response.user.username || username, // Use input username as fallback
                    email: response.user.email || '',
                    role: response.user.role || 'user',
                    name: response.user.name
                };

                setUser(userData);

                // Direct cashiers to cashier page, others to admin
                if (userData.role === 'caissier') {
                    navigate('/cashier');
                } else {
                    navigate('/admin');
                }
                return;
            }

            // If we reach here without valid user data, throw an error
            throw new Error('Invalid user data received from server');
        } catch (error) {
            console.error('Login failed:', error);
            // Rethrow to handle in the component
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Logout function
    const logout = () => {
        authService.logout();
        setUser(null);
        navigate('/');
    };

    // Value object that will be passed to consumers
    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        checkAuthStatus,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
